import styled from '@emotion/styled';
import {
  palette,
  radius,
  spacing,
  status,
  typography,
} from '../../../style-utils/theme';
import { OccupiedKind } from './range-model';

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  width: 100%;
  min-width: 0;
`;

export const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const Label = styled.p`
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
`;

export const Nav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const Months = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $count }) => $count}, minmax(0, 1fr));
  gap: ${spacing.md};
  width: 100%;
  min-width: 0;
`;

/* The polite live region — visible, so the sighted user reads the same next
   step the screen reader hears. A floor keeps the layout still as it changes. */
export const Status = styled.p`
  margin: 0;
  min-width: 0;
  min-height: ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const Line = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
  margin: 0;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  line-height: ${typography.leading.normal};
  overflow-wrap: anywhere;
`;

export const ConflictLine = styled(Line)`
  background: ${status.alert.bg};
  color: ${status.alert.fg};
`;

export const WarningLine = styled(Line)`
  background: ${status.warning.bg};
  color: ${status.warning.fg};
`;

export const ZoneNote = styled.div`
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slateLight};
`;

/* THE NIGHT MARK under a day's number. A data mark 4px tall, so the 2px radius
   is the documented small-mark exception. It is always drawn (empty when the
   night is free) so a marked and an unmarked day are the same height.
     stay            a solid bar
     owner block     hatched
     provider block  a dotted line
   The kind is ALSO spoken in the day's accessible name — the bar is never the
   only way to tell. */
const markFill = (kind: OccupiedKind | null, onFill: boolean): string => {
  const ink = onFill ? palette.onBrandStrong : palette.inkSoft;
  if (kind === 'stay') return ink;
  if (kind === 'owner_block') {
    return `repeating-linear-gradient(135deg, ${ink} 0, ${ink} 2px, transparent 2px, transparent ${spacing.half})`;
  }

  return 'transparent';
};

export const Mark = styled.span<{ $kind: OccupiedKind | null; $onFill: boolean }>`
  display: block;
  align-self: stretch;
  flex: none;
  height: ${spacing.half};
  border-radius: 2px;
  box-sizing: border-box;
  background: ${({ $kind, $onFill }) => markFill($kind, $onFill)};
  border-top: ${({ $kind, $onFill }) =>
    $kind === 'provider_block'
      ? `2px dotted ${$onFill ? palette.onBrandStrong : palette.slate}`
      : 'none'};
`;
